import * as React from 'react'

import OptionsBody from '~/client/src/shared/components/OptionsList/pieces/OptionsBody'
import OptionsEmpty from '~/client/src/shared/components/OptionsList/pieces/OptionsEmpty'
import OptionsItem from '~/client/src/shared/components/OptionsList/pieces/OptionsItem'
import OptionsItemList from '~/client/src/shared/components/OptionsList/pieces/OptionsItemList'
import OptionsListHeader from '~/client/src/shared/components/OptionsList/pieces/OptionsListHeader'
import OptionsSearch from '~/client/src/shared/components/OptionsList/pieces/OptionsSearch'
import OptionsSeparator from '~/client/src/shared/components/OptionsList/pieces/OptionsSeparator'
import OptionsToolbar from '~/client/src/shared/components/OptionsList/pieces/OptionsToolbar'
import { Badge } from '~/client/src/shared/components/shadcn-ui/badge'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { useDrillDownFilterContext } from './context'
import type { DrillDownFilterHeaderContent } from './DrillDownFilter.types'
import { ResetButton, SelectionChip } from './DrillDownFilterPieces'
import { filterDrillDownCategories } from './filterCategories'

export default function CategoryList({
  headerContent,
  autoFocusSearch = true,
}: {
  headerContent?: DrillDownFilterHeaderContent
  autoFocusSearch?: boolean
}) {
  const { state, actions, meta } = useDrillDownFilterContext()
  const showReset = state.pendingSelection.size > 0

  const categories = filterDrillDownCategories(
    meta.categories,
    state.searchTerm,
  )

  return (
    <OptionsBody>
      <OptionsToolbar>
        <OptionsSearch
          value={state.searchTerm}
          onChange={actions.setSearchTerm}
          placeholder={Localization.translator.search}
          autoFocus={autoFocusSearch}
        />
      </OptionsToolbar>

      {categories.length === 0 ? (
        <OptionsItemList>
          <OptionsEmpty />
        </OptionsItemList>
      ) : (
        <>
          <OptionsListHeader
            label={`${categories.length} ${Localization.translator.filters} / ${state.pendingSelection.size} applied`}
            action={
              showReset ? (
                <ResetButton onClick={actions.onClearAll} />
              ) : undefined
            }
          />
          <OptionsSeparator />
          {headerContent && (
            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:px-3 tw:py-2">
              {typeof headerContent === 'function'
                ? headerContent({
                    isSelected: id => state.pendingSelection.has(id),
                    toggle: actions.onToggleItem,
                  })
                : headerContent}
            </div>
          )}
          <OptionsItemList>
            {categories.map(category => {
              const selectionCount = meta.getSelectionCountForCategory(category)
              const selectedItems = meta.getSelectedItemsForCategory(category)

              return (
                <div key={category.id} className="tw:flex tw:flex-col tw:gap-1">
                  <OptionsItem
                    icon={category.icon}
                    label={category.label}
                    onClick={() => actions.onCategoryClick(category.id)}
                    trailing={
                      selectionCount > 0 ? (
                        <Badge className="tw:min-w-5 tw:px-1">
                          {selectionCount}
                        </Badge>
                      ) : undefined
                    }
                  />
                  {selectedItems.length > 0 && (
                    <div className="tw:flex tw:flex-wrap tw:gap-1.5 tw:pl-1.5 tw:pb-2">
                      {selectedItems.map(item => {
                        const itemId = category.getItemId(item)
                        return (
                          <SelectionChip
                            key={itemId}
                            label={category.getItemLabel(item)}
                            onRemove={e => {
                              e.stopPropagation()
                              actions.onToggleItem(itemId)
                            }}
                          />
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </OptionsItemList>
        </>
      )}
    </OptionsBody>
  )
}
